import React, { useCallback, useEffect, useState } from 'react';
import { useTheme } from '@flowcore-water/ui';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { definitionService } from '../services/definitionService';
import type { SkillTreeDef, Qualification, JQRItem, ItemCategory } from '../types';
import { SKILL_TREES as STATIC_SKILL_TREES } from '../data/skillTrees';
import { qualifications as staticQualifications } from '../data/qualifications';

// ── Helpers ──

const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

const nextItemId = (items: JQRItem[], category: ItemCategory): string => {
  const prefix = category === 'discussion' ? 'disc' : category === 'demonstration' ? 'demo' : 'ride';
  const existing = items.filter((i) => i.id.startsWith(prefix + '_'));
  const maxNum = existing.reduce((max, i) => {
    const n = parseInt(i.id.split('_')[1] || '0', 10);
    return n > max ? n : max;
  }, 0);
  return `${prefix}_${maxNum + 1}`;
};

// ── Sub-components ──

/** Styled button helper */
const Btn: React.FC<{
  onClick: () => void;
  label: string;
  variant?: 'primary' | 'danger' | 'ghost';
  disabled?: boolean;
  small?: boolean;
  t: Record<string, any>;
}> = ({ onClick, label, variant = 'ghost', disabled, small, t }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`rounded-md font-medium transition-colors disabled:opacity-40 ${small ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'}`}
    style={{
      background: variant === 'primary' ? t.accent : variant === 'danger' ? t.fail : 'transparent',
      color: variant === 'primary' ? t.pageBg : variant === 'danger' ? '#fff' : t.textSecondary,
      border: variant === 'ghost' ? `1px solid ${t.border}` : 'none',
    }}
  >
    {label}
  </button>
);

/** Text input */
const Field: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
  mono?: boolean;
  t: Record<string, any>;
}> = ({ label, value, onChange, disabled, placeholder, mono, t }) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs font-medium uppercase tracking-wide" style={{ color: t.textSecondary }}>{label}</label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
      className={`rounded border px-2 py-1.5 text-sm ${mono ? 'font-mono' : ''}`}
      style={{ background: t.inputBg, borderColor: t.inputBorder, color: t.textPrimary, opacity: disabled ? 0.6 : 1 }}
    />
  </div>
);

// ── JQR Item Editor ──

const JQRItemEditor: React.FC<{
  items: JQRItem[];
  onChange: (items: JQRItem[]) => void;
  qualIsNew: boolean;
  t: Record<string, any>;
}> = ({ items, onChange, qualIsNew, t }) => {
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ title: '', description: '', category: 'discussion' as ItemCategory, hasRubric: false });

  const categories: ItemCategory[] = ['discussion', 'demonstration', 'ridealong'];
  const catLabel = (c: ItemCategory) => c === 'discussion' ? 'Discussion' : c === 'demonstration' ? 'Demonstration' : 'Ridealong';
  const catColor = (c: ItemCategory) => c === 'discussion' ? '#60a5fa' : c === 'demonstration' ? '#f59e0b' : '#a78bfa';

  const startAdd = () => {
    setForm({ title: '', description: '', category: 'discussion', hasRubric: false });
    setAdding(true);
    setEditId(null);
  };

  const startEdit = (item: JQRItem) => {
    setForm({ title: item.title, description: item.description, category: item.category, hasRubric: item.hasRubric });
    setEditId(item.id);
    setAdding(false);
  };

  const saveItem = () => {
    if (!form.title.trim()) return;
    if (editId) {
      onChange(items.map((i) => i.id === editId ? { ...i, ...form } : i));
      setEditId(null);
    } else {
      const id = nextItemId(items, form.category);
      onChange([...items, { id, ...form, order: items.length }]);
      setAdding(false);
    }
    setForm({ title: '', description: '', category: 'discussion', hasRubric: false });
  };

  const removeItem = (id: string) => {
    if (!qualIsNew && !confirm('Removing a JQR item may orphan progress data. Continue?')) return;
    onChange(items.filter((i) => i.id !== id));
  };

  const moveItem = (idx: number, dir: -1 | 1) => {
    const arr = [...items];
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= arr.length) return;
    [arr[idx], arr[swapIdx]] = [arr[swapIdx], arr[idx]];
    onChange(arr.map((item, i) => ({ ...item, order: i })));
  };

  const formRow = (
    <div className="rounded border p-3 space-y-2" style={{ background: t.inputBg, borderColor: t.border }}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} t={t} />
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium uppercase tracking-wide" style={{ color: t.textSecondary }}>Category</label>
          <select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value as ItemCategory })}
            className="rounded border px-2 py-1.5 text-sm"
            style={{ background: t.inputBg, borderColor: t.inputBorder, color: t.textPrimary }}
          >
            {categories.map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}
          </select>
        </div>
      </div>
      <Field label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} t={t} />
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm" style={{ color: t.textSecondary }}>
          <input type="checkbox" checked={form.hasRubric} onChange={(e) => setForm({ ...form, hasRubric: e.target.checked })} />
          Has Rubric
        </label>
        <div className="flex-1" />
        <Btn onClick={() => { setAdding(false); setEditId(null); }} label="Cancel" t={t} small />
        <Btn onClick={saveItem} label={editId ? 'Update' : 'Add'} variant="primary" t={t} small disabled={!form.title.trim()} />
      </div>
    </div>
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide" style={{ color: t.textSecondary }}>
          JQR Items ({items.length})
        </span>
        {!adding && !editId && <Btn onClick={startAdd} label="+ Add Item" t={t} small />}
      </div>

      {items.map((item, idx) => (
        <div key={item.id}>
          {editId === item.id ? formRow : (
            <div
              className="flex items-center gap-2 rounded border px-3 py-2 text-sm"
              style={{ borderColor: t.borderSubtle, background: 'rgba(0,0,0,0.15)' }}
            >
              <div className="flex flex-col gap-0.5">
                <button onClick={() => moveItem(idx, -1)} disabled={idx === 0} className="text-[10px] leading-none disabled:opacity-20" style={{ color: t.textSecondary }}>▲</button>
                <button onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1} className="text-[10px] leading-none disabled:opacity-20" style={{ color: t.textSecondary }}>▼</button>
              </div>
              <span className="font-mono text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${catColor(item.category)}20`, color: catColor(item.category) }}>
                {item.id}
              </span>
              <span
                className="text-xs font-medium px-1.5 py-0.5 rounded"
                style={{ background: `${catColor(item.category)}15`, color: catColor(item.category) }}
              >
                {catLabel(item.category)}
              </span>
              <span className="flex-1" style={{ color: t.textPrimary }}>{item.title}</span>
              {item.hasRubric && <span className="text-[10px] font-mono px-1 rounded" style={{ background: `${t.accent}20`, color: t.accent }}>RUBRIC</span>}
              <button onClick={() => startEdit(item)} className="text-xs" style={{ color: t.accent }}>Edit</button>
              <button onClick={() => removeItem(item.id)} className="text-xs" style={{ color: t.fail }}>✕</button>
            </div>
          )}
        </div>
      ))}

      {adding && formRow}
    </div>
  );
};

// ── Qualification Editor ──

const QualEditor: React.FC<{
  qual: Qualification;
  allQualsInTree: Qualification[];
  defaultDeadlineDays: number | null;
  onSave: (qual: Qualification, deadlineDays: number | null) => void;
  onDelete: () => void;
  isNew: boolean;
  t: Record<string, any>;
}> = ({ qual, allQualsInTree, defaultDeadlineDays, onSave, onDelete, isNew, t }) => {
  const [draft, setDraft] = useState<Qualification>({ ...qual, items: [...qual.items] });
  const [deadlineDays, setDeadlineDays] = useState<string>(defaultDeadlineDays != null ? String(defaultDeadlineDays) : '');
  const [dirty, setDirty] = useState(false);

  const update = (patch: Partial<Qualification>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setDirty(true);
  };

  const otherQuals = allQualsInTree.filter((q) => q.id !== draft.id);

  const togglePrereq = (qualId: string) => {
    const has = draft.prerequisites.includes(qualId);
    update({ prerequisites: has ? draft.prerequisites.filter((p) => p !== qualId) : [...draft.prerequisites, qualId] });
  };

  return (
    <div className="space-y-4 p-4 rounded-lg border" style={{ background: t.cardBg, borderColor: t.border }}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="ID (slug)" value={draft.id} onChange={(v) => update({ id: slugify(v) })} disabled={!isNew} mono t={t} placeholder="e.g. electrical" />
        <Field label="Short Name" value={draft.shortName} onChange={(v) => update({ shortName: v })} t={t} placeholder="e.g. Electrical" />
        <Field label="Full Name" value={draft.name} onChange={(v) => update({ name: v })} t={t} placeholder="e.g. Electrical Systems" />
      </div>
      <Field label="Description" value={draft.description} onChange={(v) => update({ description: v })} t={t} />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium uppercase tracking-wide" style={{ color: t.textSecondary }}>Default Deadline (days)</label>
          <input
            type="number"
            min={1}
            value={deadlineDays}
            onChange={(e) => { setDeadlineDays(e.target.value); setDirty(true); }}
            placeholder="No deadline"
            className="rounded border px-2 py-1.5 text-sm font-mono"
            style={{ background: t.inputBg, borderColor: t.inputBorder, color: t.textPrimary }}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium uppercase tracking-wide" style={{ color: t.textSecondary }}>Optional</label>
          <label className="flex items-center gap-2 text-sm pt-1" style={{ color: t.textPrimary }}>
            <input type="checkbox" checked={draft.optional} onChange={(e) => update({ optional: e.target.checked })} />
            This qualification is optional
          </label>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium uppercase tracking-wide" style={{ color: t.textSecondary }}>Prerequisites</label>
          {otherQuals.length === 0 ? (
            <span className="text-xs pt-1" style={{ color: t.textMuted }}>No other quals in tree</span>
          ) : (
            <div className="flex flex-wrap gap-1 pt-1">
              {otherQuals.map((q) => {
                const selected = draft.prerequisites.includes(q.id);
                return (
                  <button
                    key={q.id}
                    onClick={() => togglePrereq(q.id)}
                    className="text-xs rounded-full px-2 py-0.5 border transition-colors whitespace-nowrap"
                    style={{
                      background: selected ? `${t.accent}20` : 'transparent',
                      borderColor: selected ? t.accent : t.border,
                      color: selected ? t.accent : t.textSecondary,
                    }}
                  >
                    {q.shortName || q.id}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <JQRItemEditor
        items={draft.items}
        onChange={(items) => { setDraft((d) => ({ ...d, items })); setDirty(true); }}
        qualIsNew={isNew}
        t={t}
      />

      <div className="flex items-center gap-2 pt-2" style={{ borderTop: `1px solid ${t.borderSubtle}` }}>
        <Btn
          onClick={() => onSave(draft, deadlineDays ? Number(deadlineDays) : null)}
          label={isNew ? 'Create Qualification' : 'Save Changes'}
          variant="primary"
          disabled={!draft.id || !draft.shortName || !dirty}
          t={t}
        />
        <Btn onClick={onDelete} label={isNew ? 'Cancel' : 'Delete'} variant={isNew ? 'ghost' : 'danger'} t={t} />
        {!isNew && <span className="text-xs font-mono ml-auto" style={{ color: t.textMuted }}>v{draft.version || 1}</span>}
      </div>
    </div>
  );
};

// ── Main Config Screen ──

export const SkillTreeConfig: React.FC = () => {
  const { t } = useTheme();
  const { user } = useAuth();
  const { refreshQualifications } = useData();
  const isReadOnly = user?.role === 'outside_observer';

  const [trees, setTrees] = useState<SkillTreeDef[]>([]);
  const [quals, setQuals] = useState<Qualification[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Selected tree
  const [activeTreeId, setActiveTreeId] = useState<string | null>(null);
  const [treeForm, setTreeForm] = useState<SkillTreeDef | null>(null);
  const [treeIsNew, setTreeIsNew] = useState(false);

  // Expanded qualification
  const [expandedQualId, setExpandedQualId] = useState<string | null>(null);
  const [addingQual, setAddingQual] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [t, q] = await Promise.all([
      definitionService.getSkillTrees(),
      definitionService.getQualifications(),
    ]);
    // Fall back to static seed data when the store is empty (mirrors how
    // DataContext handles qualifications — prevents the chicken-and-egg
    // problem where the admin page shows nothing because nothing was seeded)
    setTrees(t.length > 0 ? t : STATIC_SKILL_TREES);
    setQuals(q.length > 0 ? q : staticQualifications);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Select a tree
  const selectTree = (tree: SkillTreeDef) => {
    setActiveTreeId(tree.id);
    setTreeForm({ ...tree });
    setTreeIsNew(false);
    setExpandedQualId(null);
    setAddingQual(false);
  };

  const startNewTree = () => {
    const newTree: SkillTreeDef = {
      id: '',
      name: '',
      shortName: '',
      description: '',
      qualificationIds: [],
      defaultDeadlines: {},
    };
    setTreeForm(newTree);
    setTreeIsNew(true);
    setActiveTreeId(null);
    setExpandedQualId(null);
    setAddingQual(false);
  };

  const saveTree = async () => {
    if (!treeForm || !user) return;
    if (!treeForm.id || !treeForm.name) { alert('Tree ID and name are required'); return; }
    setSaving(true);
    try {
      await definitionService.saveSkillTree(treeForm, user.email);
      await load();
      setActiveTreeId(treeForm.id);
      setTreeIsNew(false);
      await refreshQualifications();
    } catch (err: any) {
      alert('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteTree = async () => {
    if (!treeForm) return;
    if (!confirm(`Delete skill tree "${treeForm.name}"? This does not delete the qualifications.`)) return;
    setSaving(true);
    try {
      await definitionService.deleteSkillTree(treeForm.id, user!.uid);
      setTreeForm(null);
      setActiveTreeId(null);
      await load();
      await refreshQualifications();
    } catch (err: any) {
      alert('Delete failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Quals for the active tree
  const treeQuals = treeForm
    ? treeForm.qualificationIds
        .map((qid) => quals.find((q) => q.id === qid))
        .filter(Boolean) as Qualification[]
    : [];

  const saveQual = async (qual: Qualification, deadlineDays: number | null) => {
    if (!treeForm || !user) return;
    setSaving(true);
    try {
      await definitionService.saveQualification(qual, user.email);

      // Update tree's qualificationIds if new qual
      const isNewToTree = !treeForm.qualificationIds.includes(qual.id);
      const updatedDeadlines = { ...treeForm.defaultDeadlines, [qual.id]: deadlineDays };
      if (deadlineDays === null) delete updatedDeadlines[qual.id];

      const updatedTree: SkillTreeDef = {
        ...treeForm,
        qualificationIds: isNewToTree ? [...treeForm.qualificationIds, qual.id] : treeForm.qualificationIds,
        defaultDeadlines: updatedDeadlines,
      };
      await definitionService.saveSkillTree(updatedTree, user.email);
      setTreeForm(updatedTree);

      await load();
      await refreshQualifications();
      setExpandedQualId(qual.id);
      setAddingQual(false);
    } catch (err: any) {
      alert('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteQual = async (qualId: string) => {
    if (!treeForm || !user) return;
    // Check if any other qual in the tree has this as a prerequisite
    const dependents = treeQuals.filter((q) => q.prerequisites.includes(qualId));
    if (dependents.length > 0) {
      alert(`Cannot delete: ${dependents.map((d) => d.shortName).join(', ')} depend on this qualification as a prerequisite.`);
      return;
    }
    if (!confirm('Delete this qualification? This may orphan existing progress data.')) return;
    setSaving(true);
    try {
      await definitionService.deleteQualification(qualId, user!.uid);
      const updatedTree: SkillTreeDef = {
        ...treeForm,
        qualificationIds: treeForm.qualificationIds.filter((id) => id !== qualId),
      };
      const { [qualId]: _, ...remainingDeadlines } = updatedTree.defaultDeadlines;
      updatedTree.defaultDeadlines = remainingDeadlines;
      await definitionService.saveSkillTree(updatedTree, user.email);
      setTreeForm(updatedTree);
      setExpandedQualId(null);
      await load();
      await refreshQualifications();
    } catch (err: any) {
      alert('Delete failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const moveQual = async (idx: number, dir: -1 | 1) => {
    if (!treeForm || !user) return;
    const ids = [...treeForm.qualificationIds];
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= ids.length) return;
    [ids[idx], ids[swapIdx]] = [ids[swapIdx], ids[idx]];
    const updatedTree = { ...treeForm, qualificationIds: ids };
    setTreeForm(updatedTree);
    // Also update order field on the quals
    for (let i = 0; i < ids.length; i++) {
      const q = quals.find((q) => q.id === ids[i]);
      if (q && q.order !== i + 1) {
        await definitionService.saveQualification({ ...q, order: i + 1 }, user.email);
      }
    }
    await definitionService.saveSkillTree(updatedTree, user.email);
    await load();
  };

  const startAddQual = () => {
    setAddingQual(true);
    setExpandedQualId(null);
  };

  const newQualTemplate: Qualification = {
    id: '',
    name: '',
    shortName: '',
    description: '',
    prerequisites: [],
    optional: false,
    order: treeQuals.length + 1,
    items: [],
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" style={{ color: t.textSecondary }}>
        Loading definitions...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ color: t.textPrimary }}>Skill Tree Configuration</h1>
        {!isReadOnly && <Btn onClick={startNewTree} label="+ New Skill Tree" variant="primary" t={t} />}
      </div>

      {/* Tree selector */}
      <div className="flex flex-wrap gap-2">
        {trees.map((tree) => (
          <button
            key={tree.id}
            onClick={() => selectTree(tree)}
            className="rounded-lg border px-4 py-2 text-sm font-medium transition-all"
            style={{
              background: activeTreeId === tree.id ? `${t.accent}15` : t.cardBg,
              borderColor: activeTreeId === tree.id ? t.accent : t.border,
              color: activeTreeId === tree.id ? t.accent : t.textSecondary,
            }}
          >
            {tree.shortName || tree.name}
            <span className="ml-2 font-mono text-xs" style={{ color: t.textMuted }}>
              ({tree.qualificationIds.length} quals)
            </span>
          </button>
        ))}
      </div>

      {/* Tree detail form */}
      {treeForm && (
        <div className="rounded-lg border p-5 space-y-5" style={{ background: t.cardBg, borderColor: t.border }}>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <Field label="Tree ID" value={treeForm.id} onChange={(v) => setTreeForm({ ...treeForm, id: slugify(v) })} disabled={!treeIsNew} mono t={t} placeholder="e.g. water_well" />
            <Field label="Short Name" value={treeForm.shortName} onChange={(v) => setTreeForm({ ...treeForm, shortName: v })} t={t} />
            <Field label="Full Name" value={treeForm.name} onChange={(v) => setTreeForm({ ...treeForm, name: v })} t={t} />
            <Field label="Description" value={treeForm.description} onChange={(v) => setTreeForm({ ...treeForm, description: v })} t={t} />
          </div>

          <div className="flex gap-2">
            {!isReadOnly && <Btn onClick={saveTree} label={treeIsNew ? 'Create Tree' : 'Save Tree'} variant="primary" disabled={!treeForm.id || !treeForm.name || saving} t={t} />}
            {!isReadOnly && !treeIsNew && <Btn onClick={deleteTree} label="Delete Tree" variant="danger" disabled={saving} t={t} />}
            {!isReadOnly && treeIsNew && <Btn onClick={() => { setTreeForm(null); setTreeIsNew(false); }} label="Cancel" t={t} />}
            {!treeIsNew && (
              <span className="ml-auto text-xs font-mono self-center" style={{ color: t.textMuted }}>
                v{treeForm.version || 1}
                {treeForm.updatedAt && ` · ${new Date(treeForm.updatedAt).toLocaleDateString()}`}
              </span>
            )}
          </div>

          {/* Qualifications list */}
          {!treeIsNew && (
            <div className="space-y-3 pt-3" style={{ borderTop: `1px solid ${t.border}` }}>
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold" style={{ color: t.textPrimary }}>
                  Qualifications ({treeQuals.length})
                </h2>
                {!isReadOnly && <Btn onClick={startAddQual} label="+ Add Qualification" variant="primary" t={t} small />}
              </div>

              {treeQuals.map((qual, idx) => (
                <div key={qual.id}>
                  {expandedQualId === qual.id ? (
                    <QualEditor
                      qual={qual}
                      allQualsInTree={treeQuals}
                      defaultDeadlineDays={treeForm.defaultDeadlines[qual.id] ?? null}
                      onSave={saveQual}
                      onDelete={() => deleteQual(qual.id)}
                      isNew={false}
                      t={t}
                    />
                  ) : (
                    <div
                      className="flex items-center gap-3 rounded-lg border px-4 py-3 cursor-pointer hover:border-opacity-80 transition-colors"
                      style={{ background: 'rgba(0,0,0,0.15)', borderColor: t.borderSubtle }}
                      onClick={() => { setExpandedQualId(qual.id); setAddingQual(false); }}
                    >
                      <div className="flex flex-col gap-0.5">
                        <button onClick={(e) => { e.stopPropagation(); moveQual(idx, -1); }} disabled={idx === 0} className="text-xs leading-none disabled:opacity-20" style={{ color: t.textSecondary }}>▲</button>
                        <button onClick={(e) => { e.stopPropagation(); moveQual(idx, 1); }} disabled={idx === treeQuals.length - 1} className="text-xs leading-none disabled:opacity-20" style={{ color: t.textSecondary }}>▼</button>
                      </div>
                      <span className="font-mono text-xs" style={{ color: t.textMuted }}>{qual.id}</span>
                      <span className="font-semibold" style={{ color: t.textPrimary }}>{qual.shortName}</span>
                      <span className="text-sm" style={{ color: t.textSecondary }}>{qual.name}</span>
                      <span className="text-xs font-mono" style={{ color: t.textMuted }}>{qual.items.length} items</span>
                      {qual.optional && <span className="text-xs rounded-full px-2 py-0.5 whitespace-nowrap" style={{ background: `${t.accent}20`, color: t.accent }}>Optional</span>}
                      {qual.prerequisites.length > 0 && (
                        <span className="text-xs" style={{ color: t.textMuted }}>
                          ← {qual.prerequisites.map((p) => quals.find((q) => q.id === p)?.shortName || p).join(', ')}
                        </span>
                      )}
                      <span className="ml-auto text-sm" style={{ color: t.textSecondary }}>▶</span>
                    </div>
                  )}
                </div>
              ))}

              {addingQual && (
                <QualEditor
                  qual={newQualTemplate}
                  allQualsInTree={treeQuals}
                  defaultDeadlineDays={null}
                  onSave={saveQual}
                  onDelete={() => setAddingQual(false)}
                  isNew={true}
                  t={t}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
